import { useEffect, useRef, useState } from "react";

type SetValue<T> = T | ((prev: T) => T);
type InitialValue<T> = T | (() => T);

function resolveInitialValue<T>(initialValue: InitialValue<T>): T {
  return typeof initialValue === "function"
    ? (initialValue as () => T)()
    : initialValue;
}

export function useLocalStorageState<T>(
  key: string,
  initialValue: InitialValue<T>,
  priorityValue?: T | null,
) {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return resolveInitialValue(initialValue);
    }

    if (priorityValue != null) return priorityValue;

    try {
      const raw = window.localStorage.getItem(key);

      if (raw !== null) {
        return JSON.parse(raw) as T;
      }
    } catch {
      // игнорируем битый JSON / недоступный storage
    }

    return resolveInitialValue(initialValue);
  });

  // Если priorityValue меняется после монтирования — синхронизируем стейт
  const prevPriorityRef = useRef(priorityValue);
  useEffect(() => {
    if (
      priorityValue != null &&
      priorityValue !== prevPriorityRef.current
    ) {
      setValue(priorityValue);
    }
    prevPriorityRef.current = priorityValue;
  }, [priorityValue]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // при желании можно логировать
    }
  }, [key, value]);

  const setStoredValue = (nextValue: SetValue<T>) => {
    setValue((prev) =>
      typeof nextValue === "function"
        ? (nextValue as (prev: T) => T)(prev)
        : nextValue,
    );
  };

  return [value, setStoredValue] as const;
}